import axios from 'axios';
import { Expediente, Stats, Usuario, Cliente, Tecnico } from '../types/expediente';

// Si REACT_APP_API_URL no está definida (undefined), se asume desarrollo
// local y se usa localhost:8033. Si está definida como cadena vacía a
// propósito (producción: nginx hace proxy de /api y /auth), se respeta
// la cadena vacía para que axios use rutas relativas al propio dominio.
export const API_BASE_URL =
  process.env.REACT_APP_API_URL === undefined
    ? 'http://localhost:8033'
    : process.env.REACT_APP_API_URL.replace(/\/$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Interfaces para la generación de documentos ---
export interface LineaFacturaRequest {
  referencia: string;
  descripcion: string;
  cantidad: number;
  precio: number;
}

export interface GenerarDocumentoRequest {
  expediente_id: string;
  tipo_documento: string;
  lineas: LineaFacturaRequest[];
  nota_adicional?: string;
  fecha_documento?: string;
  factura_id?: string;
  contenido_texto?: string;
  // Campos específicos del Dictamen Pericial
  tipo_procedimiento_completo?: string;
  descripcion_objeto_examen?: string;
  extremos_propuestos?: string[];
  observaciones_valoracion?: string;
  total_importe_palabras?: string;          // si no se envía, el backend lo genera automáticamente
  mano_obra_sin_iva?: number;               // uno de los dos; el otro se calcula por diferencia
  materiales_sin_iva?: number;
}

// Añade estas interfaces después de GenerarDocumentoRequest (línea 13)

export interface Documento {
  id: string;
  expediente_id: string;
  tipo_documento: string;
  numero_documento?: string;
  nombre_archivo?: string;
  mime_type: string;
  tamaño_kb?: number;
  estado: string;
  version: number;
  generado_automaticamente: boolean;
  fecha_generacion?: string;
  tiempo_generacion_segundos?: number;
  created_at: string;
  updated_at: string;
}

export interface DocumentoEliminado {
  message: string;
  documento_eliminado: {
    id: string;
    nombre_archivo: string;
    tipo_documento: string;
    expediente_codigo: string;
  };
}

// Interfaz mejorada para documento con datos del expediente
export interface DocumentoConExpediente extends Documento {
  expediente_codigo?: string;
  expediente_tipo?: string;
  expediente_numero?: number;
  expediente_anio?: number;
  cliente_nombre?: string;
}


// --- Interface para crear expediente - CORREGIDA ---
export interface ExpedienteCreate {
  nig?: string;
  codigo_actual?: string; // CAMBIADO: Opcional porque se genera automáticamente
  numero: number;
  anio: number;
  tipo: string;
  cliente_id?: string; // CAMBIADO: De juzgado_id a cliente_id
  tecnico_id?: string;
  observaciones_generales?: string;
  estado?: string;
  es_activo?: boolean;
  justicia_gratuita?: boolean;
}

// --- Interface para crear el expediente sucesor y vincular la cadena en un solo paso ---
export interface CrearYVincularCadenaRequest {
  codigo_actual?: string;
  codigo_original?: string;
  tipo: string;
  numero: number;
  anio: number;
  nig?: string;
  cliente_id?: string;
  tecnico_id?: string;
  justicia_gratuita?: boolean;
  observaciones_generales?: string;
}

// ==================== EXPEDIENTES ====================
export const expedientesApi = {
  getExpedientes: async (skip = 0, limit = 100): Promise<Expediente[]> => {
    const response = await api.get(`/api/expedientes?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getExpediente: async (id: string): Promise<Expediente> => {
    const response = await api.get(`/api/expedientes/${id}`);
    return response.data;
  },

  createExpediente: async (data: ExpedienteCreate): Promise<Expediente> => {
    const response = await api.post('/api/expedientes', data);
    return response.data;
  },

  updateExpediente: async (id: string, data: Partial<Expediente>): Promise<Expediente> => {
    const response = await api.put(`/api/expedientes/${id}`, data);
    return response.data;
  },

  deleteExpediente: async (id: string): Promise<void> => {
    await api.delete(`/api/expedientes/${id}`);
  },

  vincularCadena: async (
    id: string,
    campo: 'proviene_de' | 'se_ha_convertido_en',
    codigoRelacionado: string
  ): Promise<Expediente> => {
    const response = await api.post(`/api/expedientes/${id}/vincular-cadena`, {
      campo,
      codigo_relacionado: codigoRelacionado,
    });
    return response.data;
  },

  crearYVincularCadena: async (
    id: string,
    data: CrearYVincularCadenaRequest
  ): Promise<Expediente> => {
    const response = await api.post(`/api/expedientes/${id}/crear-y-vincular-cadena`, data);
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get('/api/stats');
    return response.data;
  },

  generarDocumento: async (data: GenerarDocumentoRequest): Promise<any> => {
    try {
      const response = await api.post('/api/documentos/generar', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.detail || 'Error en el servidor al generar el documento');
      } else if (error.request) {
        throw new Error('No se pudo conectar con el servidor. ¿Está en funcionamiento?');
      } else {
        throw new Error('Error inesperado al preparar la solicitud');
      }
    }
  },
};

// ==================== USUARIOS ====================
export const usuariosApi = {
  getUsuarios: async (skip = 0, limit = 100): Promise<Usuario[]> => {
    const response = await api.get(`/api/usuarios?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getUsuario: async (id: string): Promise<Usuario> => {
    const response = await api.get(`/api/usuarios/${id}`);
    return response.data;
  },

  createUsuario: async (data: Partial<Usuario>): Promise<Usuario> => {
    const response = await api.post('/api/usuarios', data);
    return response.data;
  },

  updateUsuario: async (id: string, data: Partial<Usuario>): Promise<Usuario> => {
    const response = await api.put(`/api/usuarios/${id}`, data);
    return response.data;
  },

  deleteUsuario: async (id: string): Promise<void> => {
    await api.delete(`/api/usuarios/${id}`);
  },
};

// ==================== CLIENTES ====================
export const clientesApi = {
  getClientes: async (skip = 0, limit = 100): Promise<Cliente[]> => {
    const response = await api.get(`/api/clientes?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getCliente: async (id: string): Promise<Cliente> => {
    const response = await api.get(`/api/clientes/${id}`);
    return response.data;
  },

  createCliente: async (data: Partial<Cliente>): Promise<Cliente> => {
    const response = await api.post('/api/clientes', data);
    return response.data;
  },

  updateCliente: async (id: string, data: Partial<Cliente>): Promise<Cliente> => {
    const response = await api.put(`/api/clientes/${id}`, data);
    return response.data;
  },

  deleteCliente: async (id: string): Promise<void> => {
    await api.delete(`/api/clientes/${id}`);
  },
};

// ==================== TÉCNICOS ====================
export const tecnicosApi = {
  getTecnicos: async (skip = 0, limit = 100, activosSolo = false): Promise<Tecnico[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(activosSolo && { activos_solo: 'true' })
    });
    const response = await api.get(`/api/tecnicos?${params}`);
    return response.data;
  },

  getTecnico: async (id: string): Promise<Tecnico> => {
    const response = await api.get(`/api/tecnicos/${id}`);
    return response.data;
  },

  createTecnico: async (data: Partial<Tecnico>): Promise<Tecnico> => {
    const response = await api.post('/api/tecnicos', data);
    return response.data;
  },

  updateTecnico: async (id: string, data: Partial<Tecnico>): Promise<Tecnico> => {
    const response = await api.put(`/api/tecnicos/${id}`, data);
    return response.data;
  },

  deleteTecnico: async (id: string): Promise<void> => {
    await api.delete(`/api/tecnicos/${id}`);
  },
};

// ==================== DOCUMENTOS ====================
// Añade esto después de tecnicosApi (línea 163) y antes de apiService

// ==================== DOCUMENTOS ====================
export const documentosApi = {
  // Nuevo método: Obtener todos los documentos del sistema
  getAllDocumentos: async (skip = 0, limit = 100, tipo?: string): Promise<DocumentoConExpediente[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    
    if (tipo) {
      params.append('tipo', tipo);
    }
    
    const response = await api.get(`/api/documentos?${params}`);
    return response.data;
  },

  // Método existente: Obtener documentos de un expediente específico - CORREGIDO
  getDocumentosByExpediente: async (expedienteId: string, tipo?: string): Promise<Documento[]> => {
    const params = new URLSearchParams();
    if (tipo) params.append('tipo', tipo);
    
    const response = await api.get(`/api/expedientes/${expedienteId}/documentos?${params}`);
    return response.data;
  },

  deleteDocumento: async (documentoId: string): Promise<DocumentoEliminado> => {
    const response = await api.delete(`/api/documentos/${documentoId}`);
    return response.data;
  },

  downloadDocumento: async (documentoId: string): Promise<Blob> => {
    const response = await api.get(`/api/documentos/${documentoId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }
};



// ==================== API UNIFICADA ====================
export const apiService = {
  expedientes: expedientesApi,
  usuarios: usuariosApi,
  clientes: clientesApi,
  tecnicos: tecnicosApi,
  documentos: documentosApi, // ← AÑADE ESTA LÍNEA
  
  getAllStats: async () => {
    const response = await api.get('/api/stats');
    return response.data;
  },
  
  healthCheck: async () => {
    const response = await api.get('/');
    return response.data;
  }
};




export default apiService;